<script setup>
import { Icon, Dropdown, Input, Button, ProfilePicture } from '@components'
import { ref, computed, onMounted, onUnmounted } from 'vue'

const props = defineProps({
    modelValue: {
        type: String,
    },
    options: {
        type: [Array, Object],
        default: () => []
    },

    placeholder: {
        type: String,
        default: 'Please select'
    },
    search: {
        type: Boolean,
        default: false
    },
    searchPlaceholder: {
        type: String,
        default: 'Search item within'
    },
    disabled: {
        type: Boolean,
        default: false
    },

    create: {
        type: Boolean,
        default: false
    },
    createLabel: {
        type: String,
        default: 'Create new'
    },
    createIcon: {
        type: String,
        default: 'plusCircle'
    },
    selectionContainer: {
        type: String
    },
    selectIconClass: {
        type: String
    },
    leftType: {
        type: String,
        default: 'icon',
        validator: (value) => {
            return ['icon', 'image'].includes(value)
        }
    },
    imgLeft: {
        type: String
    },
    iconLeft: {
        type: String,
    },
    iconLeftClass: {
        type: String,
    },
    optionLeft: {
        type: Boolean,
        default: false
    },
    borderless: {
        type: Boolean,
        default: false
    },
    selected: {
        type: String,
        default: ''
    },
    default: {
        type: String,
        default: ''
    },
    notFound: {
        type: String,
        default: 'No items found'
    },
    sort: {
        type: [Boolean, String],
        default: false
    },
    selector: {
        type: Boolean,
        default: true
    },
})

const isOpen = ref(false)

const toggle = () => {
    isOpen.value = !isOpen.value

    if (!isOpen.value) return

    search.value = ''

    setTimeout(() => {

        // Scroll to selected.
        const selected = document.querySelector('.select-wrapper .selected-option')

        if (selected) {
            selected.scrollIntoView({
                behavior: 'instant',
                block: 'center',
                inline: 'start'
            })
        }

        // Focus to search.
        if (props.search) {
            const search = document.querySelector('.select-wrapper .select_search_input input')
            search.focus()
        }

    }, 10)
}
const uniqueKey = ref(Math.floor(Math.random() * 100000).toString())
const selected = computed(() => props.modelValue || props.selected || props.default)
const search = ref('')

// Emitter.
const emit = defineEmits(['update:modelValue', 'change', 'create'])

const options = computed(() => {
    let items = []

    // If object.
    if (Array.isArray(props.options)) {
        props.options.forEach((option) => {
            items.push({
                label: option.label || option, // title that will be displayed
                value: option.value || option, // value that will be returned
                icon: option.icon || null, // icon that will be displayed on the left
                img: option.img || null, // image that will be displayed on the left
                disabled: option.disabled || false, // if the option is disabled
                tags: option.tags || [],
                hint: option.hint || null // example value
            })
        })
    } else {
        Object.keys(props.options).forEach((key) => {
            items.push({
                label: props.options[key],
                value: key,
                icon: null,
                img: null,
                disabled: false,
                tags: [],
                hint: null
            })
        })
    }


    if (props.search === '') return items

    items = items.filter((option) => {
        const sanitized = search.value.toLowerCase().trim()
        const value = typeof option.value === 'string' ? option.value.toLowerCase() : option.value.toString().toLowerCase()
        const tags = option.tags.map((tag) => tag.toLowerCase())

        if (typeof option.label == 'object') return

        return option.label.toLowerCase().includes(sanitized) || value.includes(sanitized) || tags.some((tag) => tag.includes(sanitized))
    })

    // Sort.
    if (props.sort) {
        items.sort((a, b) => {
            const labelA = a.label.toLowerCase()
            const labelB = b.label.toLowerCase()

            if (labelA < labelB) return -1
            if (labelA > labelB) return 1
            return 0
        })
    }

    return items
})

const label = computed(() => {
    const option = options.value.find((option) => {
        return option.value == selected.value
    })

    return option ? option.label : props.placeholder
})

const showCreateButton = computed(() => {
    if (search.value == '') return false

    let optionExists = options.value.find((option) => option.label == search.value)

    return props.create && props.search && (!optionExists || options.value.length === 0)
})

const create = () => {
    const _option = search.value
    select(_option)
    emit('create', _option)
}

const select = (opt) => {
    if (opt.disabled) return
    if (props.disabled) return

    emit('update:modelValue', opt.value)

    if (selected.value !== opt.value) {
        emit('change', opt.value)
    }

    selected.value = opt.value
    search.value = ''
    isOpen.value = false
}

const clickOutside = ($event) => {
    if (!$event.target.closest('.select-wrapper') || $event.target.closest('.select-wrapper').getAttribute('data-select') !== uniqueKey.value) {
        return isOpen.value = false
    }
}

onMounted(() => {
    document.addEventListener('click', clickOutside)
})

onUnmounted(() => {
    document.removeEventListener('click', clickOutside)
})


const selectFirst = () => {
    if (props.disabled) return

    const first = options.value[0]
    if (first) {
        select(first)
    }
}

</script>

<template>
    <div class="relative select-wrapper" :data-select="uniqueKey" tabindex="1" @blur="isOpen = false" @keydown.esc.prevent="isOpen = false">
        <!-- selection  -->
        <div tabindex="0" @keydown.enter.prevent="toggle" v-bind="$attrs" class="flex gap-2 whitespace-nowrap items-center cursor-pointer outline-none  border-[#D0D5DD] focus:shadow-[0px_0px_0px_4px_#F4EBFF,0px_1px_2px_0px_rgba(16,24,40,0.05)] focus:border-[#D6BBFB]" @click="toggle" :class="{ 'px-3.5 py-3 border border-[#D0D5DD] rounded-lg h-full justify-between': !props.borderless, 'justify-between pl-2 pr-1 mr-1.5 h-10': props.borderless }, selectionContainer">
            <!-- label  -->
            <div class="flex items-center">
                <Icon v-if="props.leftType === 'icon' && props.iconLeft" :name="props.iconLeft" size="20px" :class="`text-[#667085] mr-2 ${props.iconLeftClass}`" />
                <!-- <img v-if="props.imgLeft" :src="props.imgLeft" class="w-[20px] h-[20px] object-cover rounded-full mr-2" /> -->
                <!-- <div v-else class="w-[20px] h-[20px] rounded-full mr-2">{{ label[0] }}</div> -->
                <ProfilePicture v-if="props.leftType == 'image'" :name="label" :img="props.imgLeft" class="w-6 h-6 mr-2" textClass="!text-sm" />
                <div class="text-[#6D6F7B] text-sm w-[calc(100%-40px)]" v-html="label"></div>
            </div>
            <!-- icon  -->
            <Icon class="text-gray-500 transition" size="20px" :class="isOpen ? 'rotate-[180deg]' : ''" />
        </div>
        <!-- dropdown -->
        <transition name="slide-fade">
            <div v-if="isOpen" class="absolute z-50 flex flex-col w-full bg-white rounded-b shadow-lg top-full ">
                <!-- search  -->
                <Input v-if="props.search" @keydown.enter.prevent="selectFirst" inputContainerClass="py-[8px] px-[12px]" inputMainContainerClass="mt-[8px] mx-[12px]" class="bg-[#F2F4F7] focus:rounded-sm select_search_input mx-1" size="sm" v-model="search" :placeholder="props.searchPlaceholder" :class="props.searchClass" />
                <!-- items  -->
                <div class="flex flex-col gap-0.5 select-options overflow-y-auto max-h-96 scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-blue-400 m-1" v-if="options && options.length">
                    <div v-for="option in options" @click.prevent="select(option)" :key="option.value" @keydown.enter.prevent="select(option)" @keydown.down.prevent="select(options[options.indexOf(option) + 1])" @keydown.up.prevent="select(options[options.indexOf(option) - 1])" tabindex="0" class="outline-none flex justify-between items-center hover:text-[#6941c6] transition cursor-pointer py-3 rounded-lg font-normal text-sm hover:bg-gray-50" :class="{ 'bg-gray-50 text-[#6941c6] selected-option': option.value == selected, 'opacity-50': option.disabled, 'p-3': !props.borderless, 'pl-2': props.borderless }">
                        <div class="flex items-center gap-3 whitespace-nowrap text-[#101828] font-medium text-base w-full">
                            <Icon v-if="option.icon" :name="option.icon" />
                            <!-- <img v-if="option.img" :src="option.img" class="w-[24px] h-[24px] rounded-full" /> -->
                            <ProfilePicture v-if="props.optionLeft" :img="option.img" :name="option.label" class="w-6 h-6" textClass="!text-sm" />
                            <div class="w-full" v-html="option.label"></div>
                        </div>
                        <div v-if="option.hint" class="text-[#6D6F7B] text-sm mr-1 whitespace-nowrap">{{ option.hint }}</div>
                        <Icon v-if="option.value == selected && props.selector" name="check" class="text-[#6941c6]" size="12px" />
                    </div>
                </div>
                <!-- no items  -->
                <div v-if="!options.length" class="text-[#6D6F7B] text-sm p-3 text-center">{{ props.notFound }}</div>

                <!-- create  -->
                <div v-if="showCreateButton" class="p-1.5">
                    <Button type="tertiary_color" @click="create" class="flex items-start justify-start gap-2 w-full py-2.5 pl-2 pr-2.5 rounded-md">
                        <Icon :name="createIcon" /> {{ createLabel }} {{ search ? `'${search}'` : '' }}
                    </Button>
                </div>

            </div>
        </transition>
    </div>
</template>
